import { SIGNATURE_FLAG_TO_SCHEME } from "../cryptography/signature-scheme.mjs";
import { PasskeyPublicKey } from "../keypairs/passkey/publickey.mjs";
import { ZkLoginPublicIdentifier } from "../zklogin/publickey.mjs";
import { parseSerializedSignature } from "../cryptography/signature.mjs";
import { Ed25519PublicKey } from "../keypairs/ed25519/publickey.mjs";
import { Secp256k1PublicKey } from "../keypairs/secp256k1/publickey.mjs";
import { Secp256r1PublicKey } from "../keypairs/secp256r1/publickey.mjs";
import { Secp521r1PublicKey } from "../keypairs/secp521r1/publickey.mjs";
import { MultiSigPublicKey } from "../multisig/publickey.mjs";
import { fromBase64 } from "@safecoin/bcs";
//#region src/verify/verify.ts
/**
* Whether `signature` is a valid signature over `bytes` (and, if `options.address`
* is given, was produced by that address). Returns `false` for a malformed or
* cryptographically invalid signature, or one that doesn't match the address;
* only an *environmental* failure (e.g. a zkLogin JWK/epoch lookup) throws, so a
* network blip is never reported as an invalid signature.
*/
async function isValidSignature(bytes, signature, options = {}) {
	const parsed = tryParseSignature(signature);
	if (!parsed) return false;
	if (!await parsed.publicKey.verify(bytes, parsed.serializedSignature)) return false;
	return options.address ? parsed.publicKey.verifyAddress(options.address) : true;
}
/** Like {@link isValidSignature}, for a personal message. */
async function isValidPersonalMessageSignature(message, signature, options = {}) {
	const parsed = tryParseSignature(signature, { client: options.client });
	if (!parsed) return false;
	if (!await parsed.publicKey.verifyPersonalMessage(message, parsed.serializedSignature)) return false;
	return options.address ? parsed.publicKey.verifyAddress(options.address) : true;
}
/** Like {@link isValidSignature}, for transaction bytes. */
async function isValidTransactionSignature(transaction, signature, options = {}) {
	const parsed = tryParseSignature(signature, { client: options.client });
	if (!parsed) return false;
	if (!await parsed.publicKey.verifyTransaction(transaction, parsed.serializedSignature)) return false;
	return options.address ? parsed.publicKey.verifyAddress(options.address) : true;
}
async function verifySignature(bytes, signature, options = {}) {
	const { publicKey } = parseSignature(signature);
	if (!await isValidSignature(bytes, signature)) throw new Error(`Signature is not valid for the provided data`);
	if (options.address && !publicKey.verifyAddress(options.address)) throw new Error(`Signature is not valid for the provided address`);
	return publicKey;
}
async function verifyPersonalMessageSignature(message, signature, options = {}) {
	const { publicKey } = parseSignature(signature, options);
	if (!await isValidPersonalMessageSignature(message, signature, { client: options.client })) throw new Error(`Signature is not valid for the provided message`);
	if (options.address && !publicKey.verifyAddress(options.address)) throw new Error(`Signature is not valid for the provided address`);
	return publicKey;
}
async function verifyTransactionSignature(transaction, signature, options = {}) {
	const { publicKey } = parseSignature(signature, options);
	if (!await isValidTransactionSignature(transaction, signature, { client: options.client })) throw new Error(`Signature is not valid for the provided Transaction`);
	if (options.address && !publicKey.verifyAddress(options.address)) throw new Error(`Signature is not valid for the provided address`);
	return publicKey;
}
function parseSignature(signature, options = {}) {
	const parsedSignature = parseSerializedSignature(signature);
	if (parsedSignature.signatureScheme === "MultiSig") return {
		...parsedSignature,
		publicKey: new MultiSigPublicKey(parsedSignature.multisig.multisig_pk)
	};
	const publicKey = publicKeyFromRawBytes(parsedSignature.signatureScheme, parsedSignature.publicKey, options);
	return {
		...parsedSignature,
		publicKey
	};
}
/** {@link parseSignature}, returning `null` instead of throwing on a malformed signature. */
function tryParseSignature(signature, options = {}) {
	try {
		return parseSignature(signature, options);
	} catch {
		return null;
	}
}
function publicKeyFromRawBytes(signatureScheme, bytes, options = {}) {
	let publicKey;
	switch (signatureScheme) {
		case "ED25519":
			publicKey = new Ed25519PublicKey(bytes);
			break;
		case "Secp256k1":
			publicKey = new Secp256k1PublicKey(bytes);
			break;
		case "Secp256r1":
			publicKey = new Secp256r1PublicKey(bytes);
			break;
		case "Secp521r1":
			publicKey = new Secp521r1PublicKey(bytes);
			break;
		case "MultiSig":
			publicKey = new MultiSigPublicKey(bytes);
			break;
		case "ZkLogin":
			publicKey = ZkLoginPublicIdentifier.fromBytes(bytes, options);
			break;
		case "Passkey":
			publicKey = new PasskeyPublicKey(bytes);
			break;
		default: throw new Error(`Unsupported signature scheme ${signatureScheme}`);
	}
	if (options.address && publicKey.toSafeAddress() !== options.address) throw new Error(`Public key bytes do not match the provided address`);
	return publicKey;
}
function publicKeyFromSafeBytes(publicKey, options = {}) {
	const bytes = typeof publicKey === "string" ? fromBase64(publicKey) : publicKey;
	const signatureScheme = SIGNATURE_FLAG_TO_SCHEME[bytes[0]];
	return publicKeyFromRawBytes(signatureScheme, bytes.slice(1), options);
}
//#endregion
export { isValidPersonalMessageSignature, isValidSignature, isValidTransactionSignature, publicKeyFromRawBytes, publicKeyFromSafeBytes, verifyPersonalMessageSignature, verifySignature, verifyTransactionSignature };

//# sourceMappingURL=verify.mjs.map